<script setup lang="ts">
import { ref } from 'vue'
import { router } from '@inertiajs/vue3'
import { useScrollLock } from '@/composables/useScrollLock'

interface Props {
  occurrence: {
    schedule_id: number
    date: string
    time: string
    current_enrollments: number
    max_participants: number
    display_name: string
    topic?: string
  }
  schedule: {
    id: number
    name: string
    series_id: string
  }
}

const props = defineProps<Props>()
const emit = defineEmits<{
  close: []
  cancelled: []
}>()

// Mounted behind a v-if, so it is open for its whole lifetime
useScrollLock(() => true)

const processing = ref(false)
const error = ref<string | null>(null)

const formatOccurrenceDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-GB', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}

const formatTime = (time: string) => {
  const [hours, minutes] = time.split(':')
  const hour = parseInt(hours)
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
  return `${displayHour}:${minutes} ${period}`
}

const handleCancel = () => {
  processing.value = true
  error.value = null

  router.delete(
    `/teacher/schedules/${props.schedule.id}/occurrences/${props.occurrence.date}`,
    {
      preserveScroll: true,
      onSuccess: () => {
        emit('cancelled')
      },
      onError: (errors: any) => {
        error.value = errors.message || 'Failed to cancel session'
      },
      onFinish: () => {
        processing.value = false
      }
    }
  )
}
</script>

<template>
  <!-- Modal Overlay -->
  <Transition name="modal">
    <div
      class="fixed inset-0 z-50 overflow-y-auto"
      @click.self="emit('close')"
    >
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black/50 transition-opacity"></div>

      <!-- Modal Container -->
      <div class="flex min-h-full items-center justify-center p-4">
        <div
          class="relative w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all"
          @click.stop
        >
          <!-- Close Button -->
          <button
            type="button"
            @click="emit('close')"
            class="absolute right-4 top-4 z-10 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <!-- Header -->
          <div class="p-6 pb-4">
            <div class="flex items-start gap-4">
              <div class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div class="flex-1 pr-8">
                <h3 class="text-xl font-bold text-gray-900">Cancel Session</h3>
                <p class="mt-1 text-sm text-gray-600">
                  This action cannot be undone
                </p>
              </div>
            </div>
          </div>

          <!-- Body -->
          <div class="px-6 pb-6">
            <div class="space-y-4 text-sm text-gray-700">
              <p class="text-base">
                Are you sure you want to cancel this session?
              </p>

              <!-- Session Details Card -->
              <div class="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div class="space-y-2">
                  <div class="flex items-start gap-2">
                    <svg class="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <div class="font-semibold text-gray-900">
                        {{ formatOccurrenceDate(occurrence.date) }}
                      </div>
                      <div class="text-gray-600">
                        {{ formatTime(occurrence.time) }}
                      </div>
                    </div>
                  </div>

                  <div v-if="occurrence.topic" class="flex items-center gap-2 text-gray-600">
                    <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <span>{{ occurrence.topic }}</span>
                  </div>
                </div>
              </div>

              <!-- Warning if students are enrolled -->
              <div v-if="occurrence.current_enrollments > 0" class="rounded-lg bg-amber-50 p-4">
                <div class="flex gap-3">
                  <svg class="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div class="flex-1 text-amber-900">
                    <p class="font-semibold">
                      {{ occurrence.current_enrollments }} student{{ occurrence.current_enrollments !== 1 ? 's are' : ' is' }} enrolled
                    </p>
                    <p class="mt-1 text-sm text-amber-800">
                      All enrolled students will be automatically notified about the cancellation via email.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Info note -->
              <div class="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
                <div class="flex gap-2">
                  <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p class="text-blue-800">
                    Only this specific session will be cancelled. Other sessions in the series will continue as scheduled.
                  </p>
                </div>
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="error" class="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">
              {{ error }}
            </div>

            <!-- Actions -->
            <div class="mt-6 flex justify-end gap-3">
              <button
                type="button"
                @click="emit('close')"
                :disabled="processing"
                class="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                Keep Session
              </button>
              <button
                type="button"
                @click="handleCancel"
                :disabled="processing"
                class="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {{ processing ? 'Cancelling...' : 'Cancel Session' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
  opacity: 0;
}
</style>
